package com.arena_web.ui.sections.profile.edit

import androidx.compose.runtime.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

data class FocusActivitiesFormState(
    val sports: Set<String> = emptySet(),
    val activities: Set<String> = emptySet(),
    val lifestyle: Set<String> = emptySet(),
    val notes: Map<String, String> = emptyMap()
) {
    val totalSelected: Int get() = sports.size + activities.size + lifestyle.size
}

private const val NOTE_MAX = 300

@Composable
fun FocusActivitiesEditModal(
    isOpen: Boolean,
    initial: FocusActivitiesFormState,
    onClose: () -> Unit,
    onSave: (FocusActivitiesFormState) -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var state by remember(initial) { mutableStateOf(initial) }
    val totalSelected = state.totalSelected

    val counterRow: (@Composable () -> Unit)? = if (totalSelected > 0) {
        {
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("justify-content", "space-between")
                    property("align-items", "center")
                },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SelectedCounter(totalSelected)
                ClearAllButton(onClick = {
                    state = FocusActivitiesFormState()
                })
            }
        }
    } else null

    // Mobile → bottom sheet (Figma 8171:174103 / 8171:174363); desktop → popup.
    if (isMobile()) {
        ProfileEditSheet(
            title = t.profileFocusEditTitle,
            onClose = onClose,
            onCancel = onClose,
            onSave = { onSave(state) },
            saveEnabled = totalSelected > 0,
            counterRow = counterRow,
        ) {
            FocusActivitiesFormBody(state = state, onStateChange = { state = it })
        }
    } else {
        ProfileEditShell(
            isOpen = true,
            title = t.profileFocusEditTitle,
            description = t.profileFocusEditDescription,
            onClose = onClose,
            onCancel = onClose,
            onSave = { onSave(state) },
            saveEnabled = totalSelected > 0,
            counterRow = counterRow,
        ) {
            FocusActivitiesFormBody(state = state, onStateChange = { state = it })
        }
    }
}

/**
 * The visual body of the Focus & Activities form — search field + sport /
 * activity / lifestyle chip rows + per-selection preference-details popup.
 *
 * Extracted from [FocusActivitiesEditModal] so the Complete-Profile wizard
 * can embed the same picker in its own shell (with Next instead of Save).
 *
 * Stateless: the caller owns [state] and gets every edit through
 * [onStateChange]. Search query is internal — it does not affect saved data.
 */
@Composable
internal fun FocusActivitiesFormBody(
    state: FocusActivitiesFormState,
    onStateChange: (FocusActivitiesFormState) -> Unit,
) {
    val t = LocalAppStrings.current
    var search by remember { mutableStateOf("") }
    // When any interest is freshly picked (Sport / Activity / Lifestyle),
    // prompt for preference details (skippable).
    var preferenceTarget by remember { mutableStateOf<String?>(null) }

    fun toggleSport(id: String) {
        val freshlySelected = !state.sports.contains(id)
        val next = if (state.sports.contains(id)) state.sports - id else state.sports + id
        onStateChange(state.copy(sports = next))
        if (freshlySelected) preferenceTarget = id
    }

    fun toggleActivity(id: String) {
        val freshlySelected = !state.activities.contains(id)
        val next = if (state.activities.contains(id)) state.activities - id else state.activities + id
        onStateChange(state.copy(activities = next))
        if (freshlySelected) preferenceTarget = id
    }

    fun toggleLifestyle(id: String) {
        val freshlySelected = !state.lifestyle.contains(id)
        val next = if (state.lifestyle.contains(id)) state.lifestyle - id else state.lifestyle + id
        onStateChange(state.copy(lifestyle = next))
        if (freshlySelected) preferenceTarget = id
    }

    val searchLc = search.trim().lowercase()
    fun matches(option: InterestOption): Boolean =
        searchLc.isEmpty() || option.label.lowercase().contains(searchLc)

    // Figma 7744:308768 — Search → Sports Container gap is 18 (Form Field),
    // and inside Sports Container the three category rows have gap 28.
    Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
        SearchField(
            value = search,
            onChange = { search = it }
        )

        Column(modifier = Modifier.fillMaxWidth().sGap(28)) {
            InterestRow(
                label = t.profileFocusEditSportLabel,
                options = InterestCatalog.Sports.filter(::matches),
                selected = state.sports,
                onToggle = ::toggleSport,
                totalInGroup = InterestCatalog.Sports.size,
                selectedInGroup = state.sports.size
            )
            InterestRow(
                label = t.profileFocusEditActivityLabel,
                options = InterestCatalog.Activities.filter(::matches),
                selected = state.activities,
                onToggle = ::toggleActivity,
                totalInGroup = InterestCatalog.Activities.size,
                selectedInGroup = state.activities.size
            )
            InterestRow(
                label = t.profileFocusEditLifestyleLabel,
                options = InterestCatalog.Lifestyle.filter(::matches),
                selected = state.lifestyle,
                onToggle = ::toggleLifestyle,
                totalInGroup = InterestCatalog.Lifestyle.size,
                selectedInGroup = state.lifestyle.size
            )
        }
    }

    // Preference details edit popup (shown right after a fresh selection).
    val target = preferenceTarget
    if (target != null) {
        PreferenceDetailsPopup(
            option = InterestCatalog.lookup(target),
            initialNote = state.notes[target].orEmpty(),
            onSkip = { preferenceTarget = null },
            onAddDetails = { note ->
                val nextNotes = if (note.isBlank()) state.notes - target
                else state.notes + (target to note.trim())
                onStateChange(state.copy(notes = nextNotes))
                preferenceTarget = null
            }
        )
    }
}

@Composable
private fun SelectedCounter(count: Int) {
    val t = LocalAppStrings.current
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.sGap(4)) {
        SpanText(
            "$count",
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(14)
                .sLetterSpacing(0.014)
                .color(AppColors.Text.Secondary)
        )
        SpanText(
            t.profileFocusEditSelectedLabel,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .color(AppColors.Text.Secondary)
        )
    }
}

@Composable
private fun ClearAllButton(onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sHeight(36)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "relative")
                property("background", EDIT_PILL_BG)
            }
            .onClick { onClick() }
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "${s(13)} ${s(18)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
        ) {
            SpanText(
                t.profileFocusEditClearAll,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

@Composable
private fun SearchField(value: String, onChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(28)
            .styleModifier {
                property("position", "relative")
                // Desktop popup: caps at the 393-px Figma search width; mobile
                // sheet: clamps to the panel width so it never overflows.
                property("width", "min(${s(393)}, 100%)")
                property("background", EDIT_FIELD_BG)
            }
    ) {
        GradientBorderRing(radiusPx = 28)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "0 ${s(12)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", s(6))
                }
        ) {
            Icon(src = AppIcons.Search, modifier = Modifier.sSize(20))
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = Modifier.toAttrs {
                value(value)
                attr("placeholder", t.profileFocusEditSearchPlaceholder)
                onInput { onChange(it.value) }
                style {
                    property("flex", "1 1 0")
                    property("min-width", "0")
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", "#BED6F5")
                    property("font-family", "inherit")
                    property("font-size", s(16))
                    property("font-weight", "400")
                    property("line-height", "1.5")
                    property("letter-spacing", s(0.16))
                }
            })
        }
    }
}

@Composable
private fun InterestRow(
    label: String,
    options: List<InterestOption>,
    selected: Set<String>,
    onToggle: (String) -> Unit,
    totalInGroup: Int,
    selectedInGroup: Int
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingRight(8)
            .styleModifier {
                property("align-items", "flex-start")
                property("justify-content", "space-between")
            }
    ) {
        Column(modifier = Modifier.sGap(16).flexGrow(1)) {
            SpanText(
                "$label:",
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Light)
                    .sLineHeight(18)
                    .sLetterSpacing(0.028)
                    .color(AppColors.Text.Secondary)
            )
            if (options.isEmpty()) {
                SpanText(
                    t.profileFocusEditNoMatches,
                    modifier = Modifier
                        .sFontSize(14)
                        .color(AppColors.Text.OnSurfaceMuted)
                )
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sGap(12)
                        .styleModifier {
                            // Figma — chips wrap with row-gap 16 (vertical gap between
                            // wrapped rows of chips, e.g. Lifestyle spans 2 rows).
                            property("flex-wrap", "wrap")
                            property("row-gap", s(16))
                        }
                ) {
                    options.forEach { option ->
                        SelectableInterestChip(
                            option = option,
                            isSelected = selected.contains(option.id),
                            onClick = { onToggle(option.id) }
                        )
                    }
                }
            }
        }
        SpanText(
            "$selectedInGroup/$totalInGroup",
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}

@Composable
private fun SelectableInterestChip(
    option: InterestOption,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(22)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "relative")
                if (isSelected) {
                    property(
                        "background",
                        "linear-gradient(0deg, rgba(12, 66, 255, 0.7) 3%, rgba(102, 102, 102, 0) 60%)"
                    )
                } else {
                    property("background", EDIT_PILL_BG)
                }
                property("display", "inline-flex")
                property("align-items", "center")
            }
            .onClick { onClick() }
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .sGap(8)
                .styleModifier {
                    // Figma 7777:451794 — chip is 32px tall, inner content y=7 inside
                    // 32px frame, so vertical padding is 7 (NOT 13 from the broken MCP
                    // export). 7 + 18 (text line-height) + 7 = 32px exact.
                    property("padding", "${s(7)} ${s(14)}")
                    property("display", "inline-flex")
                    property("align-items", "center")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (option.iconSrc != null) {
                Icon(src = option.iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                option.label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

// =============================================================================
// Preference Details popup — shown over the Focus & Activities modal
// =============================================================================
@Composable
private fun PreferenceDetailsPopup(
    option: InterestOption?,
    initialNote: String,
    onSkip: () -> Unit,
    onAddDetails: (String) -> Unit
) {
    val t = LocalAppStrings.current
    var note by remember(initialNote) { mutableStateOf(initialNote.take(NOTE_MAX)) }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .backgroundColor(Color("rgba(2, 4, 11, 0.7)"))
            .onClick { onSkip() },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sBorderRadius(26)
                .sPadding(18)
                .sGap(22)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("width", "min(${s(430)}, 92vw)")
                    property("max-height", "90vh")
                    property("overflow-y", "auto")
                    property("background-color", EDIT_MODAL_SURFACE)
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // Header
            Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.sGap(8)) {
                    if (option != null) {
                        SelectableChipReadOnly(option)
                    }
                    SpanText(
                        "—",
                        modifier = Modifier
                            .sFontSize(20)
                            .color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        t.profileFocusEditPreferenceDetails,
                        modifier = Modifier
                            .sFontSize(20)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(26)
                            .sLetterSpacing(0.2)
                            .color(AppColors.Text.Secondary)
                    )
                }
                SpanText(
                    t.profileFocusEditPreferenceHint,
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .color(AppColors.Text.OnSurfaceMuted)
                )
            }

            // Note field
            Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                SpanText(
                    t.profileFocusEditNoteLabel,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .sPaddingLeft(4)
                        .color(Color("#818D9C"))
                )
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(96)
                        .sBorderRadius(18)
                        .styleModifier {
                            property("position", "relative")
                            property("background", EDIT_FIELD_BG)
                        }
                ) {
                    GradientBorderRing(radiusPx = 18)
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("padding", s(12))
                                property("display", "flex")
                            }
                    ) {
                        TextArea(attrs = Modifier.toAttrs {
                            value(note)
                            attr("maxlength", NOTE_MAX.toString())
                            attr("placeholder", t.profileFocusEditNotePlaceholder)
                            onInput { note = it.value.take(NOTE_MAX) }
                            style {
                                property("flex", "1 1 0")
                                property("min-width", "0")
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("resize", "none")
                                property("color", "#BED6F5")
                                property("font-family", "inherit")
                                property("font-size", s(16))
                                property("font-weight", "300")
                                property("line-height", "1.5")
                                property("letter-spacing", s(0.16))
                            }
                        })
                    }
                }
                Row(
                    modifier = Modifier.fillMaxWidth().styleModifier { property("justify-content", "flex-end") }
                ) {
                    SpanText(
                        "${note.length} / $NOTE_MAX",
                        modifier = Modifier
                            .sFontSize(10)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(12)
                            .sLetterSpacing(0.1)
                            .color(LocalSportColors.current.metricsPrimary)
                    )
                }
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(12)
                    .styleModifier { property("justify-content", "flex-end") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                GhostPillButton(t.commonSkip, onClick = onSkip)
                PrimaryOrGhostPillButton(t.profileFocusEditAddDetails, primary = true, onClick = { onAddDetails(note) })
            }
        }
    }
}

@Composable
private fun SelectableChipReadOnly(option: InterestOption) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(22)
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    "linear-gradient(0deg, rgba(12, 66, 255, 0.7) 3%, rgba(102, 102, 102, 0) 60%)"
                )
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .sGap(8)
                .styleModifier {
                    // Figma 7777:451794 — chip is 32px tall, inner content y=7 inside
                    // 32px frame, so vertical padding is 7 (NOT 13 from the broken MCP
                    // export). 7 + 18 (text line-height) + 7 = 32px exact.
                    property("padding", "${s(7)} ${s(14)}")
                    property("display", "inline-flex")
                    property("align-items", "center")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (option.iconSrc != null) {
                Icon(src = option.iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                option.label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

